import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Star, Plus, X, Pencil, ExternalLink, GripVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  getFavorites,
  createFavorite,
  updateFavorite,
  deleteFavorite,
  type UserFavorite,
} from "@/lib/api";

const MAX_FAVORITES = 10;

export function FavoritesDropdown() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [manageOpen, setManageOpen] = useState(false);
  const [addLabel, setAddLabel] = useState("");
  const [addUrl, setAddUrl] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editUrl, setEditUrl] = useState("");

  // Drag state
  const dragIndex = useRef<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const { data: favorites = [] } = useQuery<UserFavorite[]>({
    queryKey: ["user-favorites"],
    queryFn: getFavorites,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["user-favorites"] });

  const addMut = useMutation({
    mutationFn: () => createFavorite(addLabel.trim(), addUrl.trim()),
    onSuccess: () => { invalidate(); setAddLabel(""); setAddUrl(""); },
    onError: (e: any) => toast({ description: e.message ?? "Failed to add", variant: "destructive" }),
  });

  const editMut = useMutation({
    mutationFn: ({ id }: { id: string }) => updateFavorite(id, { label: editLabel.trim(), url: editUrl.trim() }),
    onSuccess: () => { invalidate(); setEditingId(null); },
    onError: (e: any) => toast({ description: e.message ?? "Failed to save", variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteFavorite,
    onSuccess: invalidate,
  });

  const reorderMut = useMutation({
    mutationFn: async (reordered: UserFavorite[]) => {
      await Promise.all(
        reordered.map((fav, i) =>
          fav.sort_order !== i ? updateFavorite(fav.id, { sort_order: i }) : Promise.resolve(fav)
        )
      );
    },
    onSuccess: invalidate,
  });

  function handleClick(url: string) {
    if (url.startsWith("/")) navigate(url);
    else window.open(url, "_blank", "noopener,noreferrer");
  }

  function startEdit(fav: UserFavorite) {
    setEditingId(fav.id);
    setEditLabel(fav.label);
    setEditUrl(fav.url);
  }

  function handleAddSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!addLabel.trim() || !addUrl.trim()) return;
    addMut.mutate();
  }

  function handleDragStart(i: number) {
    dragIndex.current = i;
  }

  function handleDragOver(e: React.DragEvent, i: number) {
    e.preventDefault();
    setHoverIndex(i);
  }

  function handleDrop(e: React.DragEvent, dropIndex: number) {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === dropIndex) {
      setHoverIndex(null);
      return;
    }
    const reordered = [...favorites];
    const [moved] = reordered.splice(dragIndex.current, 1);
    reordered.splice(dropIndex, 0, moved);
    // Optimistic update
    qc.setQueryData(["user-favorites"], reordered);
    reorderMut.mutate(reordered);
    dragIndex.current = null;
    setHoverIndex(null);
  }

  function handleDragEnd() {
    dragIndex.current = null;
    setHoverIndex(null);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1 px-3 h-9 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors">
          <Star className="w-3.5 h-3.5" />
          Favorites
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {favorites.length === 0 && (
          <div className="px-3 py-2 text-xs text-muted-foreground">No favorites yet.</div>
        )}
        {favorites.map((fav) => (
          <DropdownMenuItem
            key={fav.id}
            onClick={() => handleClick(fav.url)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <span className="flex-1 truncate">{fav.label}</span>
            {!fav.url.startsWith("/") && <ExternalLink className="w-3 h-3 shrink-0 opacity-40" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild onSelect={(e) => e.preventDefault()}>
          <Popover open={manageOpen} onOpenChange={setManageOpen}>
            <PopoverTrigger asChild>
              <button className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Plus className="w-3.5 h-3.5" />
                Manage favorites
              </button>
            </PopoverTrigger>
            <PopoverContent side="right" align="start" className="w-80 p-3">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">My Favorites</p>
                  <span className="text-xs text-muted-foreground">{favorites.length}/{MAX_FAVORITES}</span>
                </div>

                {favorites.length > 0 && (
                  <div className="space-y-0.5 max-h-52 overflow-y-auto">
                    {favorites.map((fav, i) => (
                      <div
                        key={fav.id}
                        draggable={editingId !== fav.id}
                        onDragStart={() => handleDragStart(i)}
                        onDragOver={(e) => handleDragOver(e, i)}
                        onDrop={(e) => handleDrop(e, i)}
                        onDragEnd={handleDragEnd}
                        className={`group rounded transition-colors ${
                          hoverIndex === i && dragIndex.current !== i
                            ? "border-t-2 border-primary"
                            : ""
                        }`}
                      >
                        {editingId === fav.id ? (
                          <div className="space-y-1 p-2 bg-muted/50 rounded">
                            <Input
                              value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              placeholder="Label"
                              className="h-7 text-xs"
                              autoFocus
                            />
                            <Input
                              value={editUrl}
                              onChange={(e) => setEditUrl(e.target.value)}
                              placeholder="URL"
                              className="h-7 text-xs"
                            />
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setEditingId(null)}>Cancel</Button>
                              <Button size="sm" className="h-6 text-xs px-2" onClick={() => editMut.mutate({ id: fav.id })}>Save</Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 px-1 py-1 rounded hover:bg-muted/60 cursor-grab active:cursor-grabbing">
                            <GripVertical className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-muted-foreground shrink-0" />
                            <span className="flex-1 text-xs truncate">{fav.label}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); startEdit(fav); }}
                              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity p-0.5"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteMut.mutate(fav.id); }}
                              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-rose-500 transition-opacity p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {favorites.length < MAX_FAVORITES && (
                  <form onSubmit={handleAddSubmit} className="space-y-1.5 border-t pt-2">
                    <p className="text-xs text-muted-foreground font-medium">Add favorite</p>
                    <Input
                      value={addLabel}
                      onChange={(e) => setAddLabel(e.target.value)}
                      placeholder="Label (e.g. Tax Returns)"
                      className="h-7 text-xs"
                    />
                    <Input
                      value={addUrl}
                      onChange={(e) => setAddUrl(e.target.value)}
                      placeholder="URL (e.g. /accounts or https://...)"
                      className="h-7 text-xs"
                    />
                    <Button type="submit" size="sm" className="h-7 text-xs w-full" disabled={!addLabel.trim() || !addUrl.trim() || addMut.isPending}>
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </form>
                )}

                {favorites.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-1">No favorites yet. Add one above.</p>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
